import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  styled,
  Tab,
  TabProps,
  Tabs,
  Typography,
} from "@mui/material";
import SectionSlider from "../Reusable/SectionSlider/SectionSlider";
import { sections, sectionPaths } from '../../constants/sectionData';
import ActivityBar from "../Reusable/ActivityBar/ActivityBar";
import pdf from '../../assets/images/fileImage/pdf.png';
import image from '../../assets/images/fileImage/image_icon.png';
import GlobalPoliciesFileViewPopup from '../Reusable/Popup/GlobalPoliciesFileViewPopup';
import GPAdminFileViewPopup from '../Reusable/Popup/GPAdminFileViewPopup';
import { apiService } from "../../api/apiService";
import RoutineTabs from "../Reusable/utils/RoutineTabs";
import CustomerCentricityPopup from "../Reusable/Routine/Popup/CustomerCentricityPopup";
import CustomerCentericityTabs from "../Reusable/utils/CustomerCentericityTabs";
import { getFileImagePath } from "../Reusable/utils/fileUtils";
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';


// Styled Tabs Component
const StyledTabs = styled(Tabs)({
  '& .MuiTabs-flexContainer': {
    gap: '20px', // Add 100px gap between tabs
    padding: '0 25px',
  },
  '& .MuiTabs-indicator': {
    // backgroundColor: 'primary.main', 
  },
});

// Styled Tab Component
const StyledTab = styled((props: TabProps & { selected?: boolean }) => <Tab {...props} />)(({ theme, selected }) => ({
  borderRadius: '11px 11px 0px 0px',
  backgroundColor: '#FFF',
  color: selected ? '#000' : '#000',
  width: '400px',
  textAlign: 'center',
  padding: '15px',
  transition: 'background-color 0.3s, color 0.3s',
  '&:hover': {
    // backgroundColor: theme.palette.primary.light,
    // color: theme.palette.primary.contrastText,
  },
}));

interface LocationState {
  management?: string;
  id: any;
  function_l1: any;
  file_name: string;
  created_at: any;
  updated_at: any;
  updated_by: any;
  onClick: (id: any, file_name: string, function_l1: any, created_at: any, updated_at: any, updated_by: any) => void;
  status: any;
  reason_to_approve: any;
  reason_to_revert: any;
}

interface GlobalPoliciesUploadProps {
  isAdmin?: boolean;
  isManager?: boolean;
}

interface FilterValues {
  function_l1: string;
  function_l2: string;
  function_l3: string;
  function_l4: string;
}

const CustomerCentricity: React.FC<GlobalPoliciesUploadProps> = ({ isAdmin, isManager }) => {
  const { instance } = useMsal();
  const management = localStorage.getItem("management") || "Default Location Name";

  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("1");
  const [verticalTabValue, setVerticalTabValue] = useState("a");
  const [openUpload, setOpenUpload] = useState(false);
  const [open, setOpen] = useState(false);
  const [routineData, setRoutineData] = useState<any[]>([]);
  const [value, setValue] = useState(0); // Main tab value
  const [verticalTabs, setVerticalTabs] = useState([
    { label: "NPS Governance", value: "a" },
    { label: "Stakeholder connects", value: "b" },
    { label: "Action Plans", value: "c" },
    { label: "Hotspots", value: "d" },
  ]);

  const getFileQuestions = (tabValue: string) => {
    if (value === 0){
      switch (tabValue) {
        case "a":
          return "Question : NPS governance established for critical & non critical processes which are routine in nature(without tickets), including measurement, routines with stakeholders and BSC leadership, and action plans for addressing detractors and passives";
        case "d":
            return "Question : NPS hotspot concept is implemented to identify critical teams or processes in the BSC with negative business experience";
        default:
          return null;
      }
    }else if (value === 1){
      switch (tabValue) {
        case "a":
          return "Question : BSC follows the plan to deploy Global Contact Model Platform for its relevant services or has an analysis in place on AS IS WoW for managing service requests in the center";
        case "b":
          return "Question : Action plans are in place for addressing ticket resolution quality issues, including reopening, expired cases, and SLAs not being met";
        default:
          return null;
      }
    }


  };


  // Update verticalTabs based on the main tab value AND management
  useEffect(() => {
    if (value === 0) {
      // Business Experience management via NPS - same for all locations
      setVerticalTabs([
        { label: "NPS Governance", value: "a" },
        { label: "Stakeholder connects", value: "b" },
        { label: "Action Plans", value: "c" },
        { label: "Hotspots", value: "d" },
      ]);
    } else if (value === 1) {
      // Contact Model - different based on location
      const specialLocations = [
        "Prague & India GCC", 
        "Xiamen, BSC", 
        "Johannesburg, BSC",
        "Aguascalientes, BSC",
      ];
      
      if (specialLocations.includes(management)) {
        setVerticalTabs([
          { label: "Contact Model Platform", value: "a" },
          { label: "SLA Action Plans (Contact Model)", value: "b" },
          { label: "Process Identification - Service Request", value: "c" },
        ]);
      } else {
        setVerticalTabs([
          { label: "Contact Model Platform", value: "a" },
          { label: "SLA Action Plans (Contact Model)", value: "b" },
        ]);
      }
    }
    // Reset verticalTabValue to the first tab whenever verticalTabs changes
    setVerticalTabValue("a");
  }, [value, management]); // Add management to dependencies

  const handleChange = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [filterValue, setFilterValue] = useState('');
  const [files, setFiles] = useState<LocationState[]>([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 3;

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Tab Status Logic
  const tabStatuses: { label: string; value: 'All' | 'Pending' | 'Approved' | 'Reverted'; apiStatus: string | null }[] = [
    { label: 'All', value: 'All', apiStatus: null },
    { label: 'Pending', value: 'Pending', apiStatus: 'Pending' },
    { label: 'Approved', value: 'Approved', apiStatus: 'Approved' },
    { label: 'Reverted', value: 'Reverted', apiStatus: 'Reverted' },
  ];

  const selectedStatus = tabStatuses.find(tab => String(tabStatuses.indexOf(tab) + 1) === tabValue)?.value;
  const filteredRoutineData = selectedStatus === "All"
    ? routineData
    : routineData.filter(item => item.status === selectedStatus);

  const paginatedData = filteredRoutineData.slice(startIndex, endIndex);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

   const [filterValues, setFilterValues] = useState<FilterValues>({
      function_l1: '',
      function_l2: '',
      function_l3: '',
      function_l4: ''
    });
    

  // Update handleApplyFilter to handle all levels

const handleApplyFilter = (function_l1: string, function_l2: string, function_l3: string, function_l4: string) => {
  const newFilters = {
    function_l1,
    function_l2,
    function_l3,
    function_l4
  };
  setFilterValues(newFilters);
  setPage(1);
  fetchRoutineData(newFilters);
};


  const fetchRoutineData = async (filters: Partial<FilterValues> = {})  => {
    setIsLoading(true);
    try {
      const response = await apiService(
        "/management/filter_customer_centricity",
        "GET",
        {
          management: management,
          section_name: verticalTabs.find((tab) => tab.value === verticalTabValue)?.label,
          ...filters, // Spread the filters object to include all filter values
        },
        undefined,
        instance as PublicClientApplication
      );

      if (response.data) {
        setRoutineData(response.data);
        setFiles(response.data);
      }
    } catch (error) {
      console.error("Error fetching routine data:", error);
      setRoutineData([]);
      if (error instanceof Error) {
        setError(error.message || "An error occurred");
      } else {
        setError("An error occurred");
      }
      if ((error as any).response && (error as any).response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {

    fetchRoutineData();
  }, [verticalTabValue, verticalTabs]);

  const handleVerticalTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setVerticalTabValue(newValue);
      // Clear all filters when tab changes
  setFilterValues({
    function_l1: '',
    function_l2: '',
    function_l3: '',
    function_l4: ''
  });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [selectedFile, setSelectedFile] = useState<{ id: any; file_name: string, function_l1: any, created_at: any, updated_at: any, updated_by: any, status: any, reason_to_approve: any, reason_to_revert: any } | null>(null);

  const handleViewClick = (id: any, file_name: string, function_l1: any, created_at: any, updated_at: any, updated_by: any, status: any, reason_to_approve: any, reason_to_revert: any) => {
    setPopupOpen(true);
    setSelectedFile({ id, file_name, function_l1, created_at, status, reason_to_approve, reason_to_revert, updated_at, updated_by });
  };

  const breadcrumbs = [
    <Link key="1" color="#000" to="/">
      Home
    </Link>,
    <Link key="2" color="#000" to="/operations/management">
      Evidence Upload
    </Link>,
    <Link key="3" color="#000" to="/operations/management">
      Management
    </Link>,
    <Typography key="4" sx={{ fontFamily: 'Avantt' }}>
      {management}
    </Typography>,
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const counts = {
    All: files.length,
    Pending: files.filter((file) => file.status === 'Pending').length,
    Approved: files.filter((file) => file.status === 'Approved').length,
    Reverted: files.filter((file) => file.status === 'Reverted').length,
  };

  return (
    <>
      <ActivityBar breadcrumbs={breadcrumbs} />
      <SectionSlider
        sections={sections}
        sectionPaths={sectionPaths}
        highlightSection={'Customer Centricity'}
        highlightColor="#D7A833"
        defaultColor="#666"
      />

      <Box sx={{ width: '100%' }}>
        <StyledTabs value={value} onChange={handleChange} aria-label="main tabs">
          <StyledTab
            label="Business Experience management via NPS"
            selected={value === 0}
          />
          <StyledTab
            label="Contact Model"
            selected={value === 1}
            sx={{ width: '320px' }}
          />
        </StyledTabs>
      </Box>

      <CustomerCentericityTabs
        tabValue={tabValue}
        question={getFileQuestions(verticalTabValue)}

        handleTabChange={handleTabChange}
        tabStatuses={tabStatuses}
        counts={counts}
        verticalTabValue={verticalTabValue}
        verticalTabs={verticalTabs}
        isLoading={isLoading}
        filteredRoutineData={filteredRoutineData}
        paginatedData={paginatedData}
        routineData={routineData}
        itemsPerPage={itemsPerPage}
        handleChangePage={handleChangePage}
        handleViewClick={handleViewClick}
        setOpenUpload={setOpenUpload}
        getFileImagePath={getFileImagePath}
        handleVerticalTabChange={handleVerticalTabChange}
        isMcrTab={true}
        handleApplyFilter={handleApplyFilter}
        level3={true}
        initialValues={{
          l1: filterValues.function_l1,
          l2: filterValues.function_l2,
          l3: filterValues.function_l3,
          l4: filterValues.function_l4
        }}
        functionL1={filterValues.function_l1}
        functionL2={filterValues.function_l2}
        functionL3={filterValues.function_l3}
        functionL4={filterValues.function_l4}
        onSuccess={fetchRoutineData}
        reuploadapiEndpoint="/management/customer_centricity"
        
      />

      <CustomerCentricityPopup openUpload={openUpload} setOpenUpload={setOpenUpload} management={management} verticalTabValue={verticalTabs.find(tab => tab.value === verticalTabValue)?.label} onSuccess={fetchRoutineData} />

      {isPopupOpen && selectedFile && (
        <>
          {isManager && (
            <GlobalPoliciesFileViewPopup
              onClose={handleClosePopup}
              open={true}
              name={selectedFile.file_name}
              id={selectedFile.id}
              functionl1={selectedFile.function_l1}
              date={selectedFile.updated_at}
              updatedBy={selectedFile.updated_by}
              sectionTitle={verticalTabs.find(tab => tab.value === verticalTabValue)?.label || ''}
              userDetail={true}
              apiPath={`/management/customer_centricity/${selectedFile.id}`}
            />
          )}
          {isAdmin && (
            <GPAdminFileViewPopup
              onClose={handleClosePopup}
              open={true}
              name={selectedFile.file_name}
              id={selectedFile.id}
              functionl1={selectedFile.function_l1}
              date={selectedFile.updated_at}
              updatedBy={selectedFile.updated_by}
              status={selectedFile.status}
              reason_to_approve={selectedFile.reason_to_approve}
              reason_to_revert={selectedFile.reason_to_revert}
              sectionTitle={verticalTabs.find(tab => tab.value === verticalTabValue)?.label || ''}
              apiPath={`/management/customer_centricity/${selectedFile.id}`}
              statusApiPath="/management/customer_centricity_status"
              onSuccess={fetchRoutineData} // Pass the fetchRoutineData function to refresh data after closing the popup
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomerCentricity;